import React, { useState, useEffect } from "react";
import type { BraillerLoanManagementItem, BraillerCatalogItemSummary } from '../brailler.types';
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { SelectField, TextField, Button } from "../../../../Components/Inputs";
import { getReplacementStatusLabel } from "./braillerLoanManagementUtility";
import { BRAILLER_REPLACEMENT_STATUS } from "../braillerConstants";

export const BraillerReplacementDialog = ({
    isOpen,
    onClose,
    repairItem,
    catalogItems,
    isLoading,
    onCreateReplacementLoan
}: {
    isOpen: boolean;
    onClose: () => void;
    repairItem: BraillerLoanManagementItem | null;
    catalogItems: BraillerCatalogItemSummary[];
    isLoading: boolean;
    onCreateReplacementLoan: (repairId: string, serialNumber: string, catalogItemId: string, shelfLocation: string) => void;
}) => {
    const [serialNumber, setSerialNumber] = useState('');
    const [selectedCatalogId, setSelectedCatalogId] = useState<string | null>(null);
    const [shelfLocation, setShelfLocation] = useState('TBD');

    // Reset form when dialog opens
    useEffect(() => {
        if (isOpen) {
            setSerialNumber('');
            setSelectedCatalogId(null);
            setShelfLocation('TBD');
        }
    }, [isOpen]);

    const handleSerialNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSerialNumber(event.target.value);
    };

    const handleCatalogChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedCatalogId(event.target.value || null);
    };

    const handleShelfLocationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShelfLocation(event.target.value);
    };

    const handleCreateReplacement = () => {
        if (!repairItem) return;
        const repairId = repairItem.braillerRepairId || repairItem.id;
        onCreateReplacementLoan(repairId, serialNumber, selectedCatalogId || '', shelfLocation);
    };

    const handleCancelClick = () => {
        onClose();
    };

    const catalogOptions = (catalogItems || []).map((item: BraillerCatalogItemSummary) => ({
        value: item.braillerCatalogId,
        text: `${item.title} - ${item.model || 'No Model'}`
    }));

    const replacementPreferenceLabel = getReplacementStatusLabel(repairItem?.replacementPreference ?? null);
    const needsReplacement = repairItem?.replacementPreference && 
        repairItem.replacementPreference !== BRAILLER_REPLACEMENT_STATUS.NotNeeded.id;

    const canSubmit = serialNumber.trim().length > 0;

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create Replacement Brailler Loan</DialogTitle>
            <DialogContent>
                {!needsReplacement ? (
                    <p>This repair does not have a replacement preference set.</p>
                ) : repairItem?.hasReplacementLoan ? (
                    <p>A replacement loan has already been created for this repair.</p>
                ) : (
                    <>
                        <div style={{ marginBottom: '16px' }}>
                            <strong>Repair Issue:</strong> {repairItem?.issue || 'N/A'}
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <strong>Original Serial Number:</strong> {repairItem?.serialNumber || 'N/A'}
                        </div>
                        <div style={{ marginBottom: '16px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                            <strong>Replacement Preference:</strong> {replacementPreferenceLabel}
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <strong>Patron:</strong> {repairItem?.patronName || repairItem?.studentName || 'N/A'}
                        </div>

                        {isLoading ? (
                            <p>Loading catalog items...</p>
                        ) : (
                            <>
                                <TextField
                                    label="Serial Number"
                                    name="replacementSerialNumber"
                                    value={serialNumber}
                                    onChange={handleSerialNumberChange}
                                    helpText="Enter the serial number of the replacement brailler. If it exists and is available, it will be used. If not found, a new inventory item will be created."
                                    required
                                />

                                <SelectField
                                    label="Catalog Type"
                                    name="catalogType"
                                    onChange={handleCatalogChange}
                                    options={catalogOptions}
                                    value={selectedCatalogId || ''}
                                    includeDefaultOption={true}
                                    helpText="Required only if the serial number doesn't exist in the system. Select the catalog type for the new inventory item."
                                />

                                <TextField
                                    label="Shelf Location"
                                    name="shelfLocation"
                                    value={shelfLocation}
                                    onChange={handleShelfLocationChange}
                                    helpText="Shelf location for the new inventory item (defaults to 'TBD')"
                                />
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    name="btnCancelReplacement"
                    onClick={handleCancelClick}
                    label="Cancel"
                    isCollapsed
                    disabled={isLoading}
                />
                {needsReplacement && !repairItem?.hasReplacementLoan && (
                    <Button
                        name="btnCreateReplacementLoan"
                        onClick={handleCreateReplacement}
                        label="Create Replacement Loan"
                        isCollapsed
                        isPrimary
                        disabled={isLoading || !canSubmit}
                    />
                )}
            </DialogActions>
        </Dialog>
    );
};
